import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MenuBar from "./MenuBar";
import { apiFetch } from "./api";

import { useI18n } from "./i18n/I18nContext";

const LABELS = {
  couleurs: "Couleurs",
  coupes: "Coupes",
  essences: "Essences",
  fournisseurs: "Fournisseurs",
  dimamorceur: "Dimensions Amorceur", // ✅
};

export default function OptionsManager() {
  const { t, ui } = useI18n();

  const { type } = useParams(); // "couleurs" | "coupes" | "essences" | "fournisseurs"
  const navigate = useNavigate();
  const [data, setData] = useState({
    essences: [],
    couleurs: [],
    coupes: [],
    fournisseurs: [],
    dimamorceur: [],
  });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [code, setCode] = useState("");
  const [Nom, setNom] = useState("");

  const title = ui(LABELS[type] || type);

  // Charger les options
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/options");
        const json = await res.json();
        if (!cancelled)
          setData({
            essences: json.essences || [],
            couleurs: json.couleurs || json.coloris || [],
            coupes: json.coupes || [],
            fournisseurs: json.fournisseurs || [],
            dimamorceur: json.dimamorceur || [], // ✅ cohérent avec backend
          });
      } catch (e) {
        if (!cancelled) setMsg("Erreur de chargement.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type]);

  const items = useMemo(() => data[type] || [], [data, type]);

  const refresh = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await apiFetch("/api/options");
      const json = await res.json();
      setData({
        essences: json.essences || [],
        couleurs: json.couleurs || json.coloris || [],
        coupes: json.coupes || [],
        fournisseurs: json.fournisseurs || [],
        dimamorceur: json.dimamorceur || [], // ✅ cohérent avec backend
      });
    } catch (e) {
      setMsg("Erreur lors de l’actualisation.");
    } finally {
      setLoading(false);
    }
  };

  // Ajouter
  const onAdd = async () => {
    if (!code.trim() || !Nom.trim()) {
      setMsg("Code et nom requis.");
      return;
    }
    try {
      setMsg("Ajout en cours…");
      const r = await apiFetch(`/api/options/${type}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim(), nom: Nom.trim() }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok || out.status === "error")
        throw new Error(out.message || "Erreur API");
      setCode("");
      setNom("");
      await refresh();
      setMsg("✅ Ajout effectué.");
    } catch (e) {
      setMsg(e.message || "Erreur ajout.");
    }
  };

  // Supprimer
  const onDelete = async (c) => {
    if (!window.confirm(`Supprimer l’élément ${c} ?`)) return;
    try {
      setMsg("Suppression…");
      const r = await apiFetch(
        `/api/options/${type}/${encodeURIComponent(c)}`,
        {
          method: "DELETE",
        },
      );
      const out = await r.json().catch(() => ({}));
      if (!r.ok || out.status === "error")
        throw new Error(out.message || "Erreur API");
      await refresh();
      setMsg("✅ Suppression effectuée.");
    } catch (e) {
      setMsg(e.message || "Erreur suppression.");
    }
  };

  return (
    <div style={page}>
      <MenuBar active="logistique" />
      <div style={wrap}>
        <section style={panel}>
          <div style={titleStyle}>{`Gestion des ${title}`}</div>

          <div style={toolbar}>
            <button onClick={() => navigate(-1)} style={btn}>
              ← {ui("Retour")}
            </button>
            <button onClick={refresh} style={btn}>
              Actualiser
            </button>
          </div>

          <div style={grid}>
            <div style={box}>
              <div style={boxTitle}>{ui("Ajouter")}</div>
              <div style={row}>
                <label style={lbl}>Code</label>
                <input
                  style={inp}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                />
              </div>
              <div style={row}>
                <label style={lbl}>Nom</label>
                <input
                  style={inp}
                  value={Nom}
                  onChange={(e) => setNom(e.target.value)}
                />
              </div>
              <div style={{ textAlign: "right" }}>
                <button onClick={onAdd} style={btnPrimary}>
                  {ui("Ajouter")}
                </button>
              </div>
            </div>

            <div style={box}>
              <div style={boxTitle}>Liste</div>
              {loading ? (
                <div style={{ padding: 10 }}>{ui("Chargement…")}</div>
              ) : (
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Code</th>
                      <th style={th}>Nom</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.code}>
                        <td style={td}>{it.code}</td>
                        <td style={td}>{it.nom}</td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ ...td, textAlign: "center" }}>
                          Aucun élément
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {msg && <div style={{ marginTop: 10, fontWeight: 700 }}>{msg}</div>}
        </section>
      </div>
    </div>
  );
}

/* styles simples */
const page = {
  minHeight: "100vh",
  background: "#000",
  color: "#fff",
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};
const wrap = { padding: "0 36px 36px" };
const panel = {
  background: "#63686f",
  border: "1px solid #3c4046",
  padding: 16,
  marginTop: 14,
};
const titleStyle = {
  color: "#fff",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  marginBottom: 10,
};
const toolbar = { display: "flex", gap: 10, marginBottom: 10 };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const box = { background: "#e9eaed", border: "1px solid #c9cbd1", padding: 12 };
const boxTitle = {
  background: "#d8d9db",
  color: "#000",
  fontWeight: 800,
  textTransform: "uppercase",
  padding: "8px 10px",
  margin: "-12px -12px 12px -12px",
  borderBottom: "1px solid #c9cbd1",
};
const row = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  alignItems: "center",
  gap: 8,
  margin: "6px 0",
};
const lbl = { color: "#000", fontWeight: 700 };
const inp = {
  height: 34,
  border: "1px solid #c0c0c0",
  padding: "0 8px",
  background: "#fff",
  width: "100%",
};
const table = { width: "100%", borderCollapse: "collapse", background: "#fff" };
const th = {
  border: "1px solid #c9cbd1",
  padding: "8px",
  background: "#f3f4f6",
  color: "#000",
  textAlign: "left",
};
const td = { border: "1px solid #c9cbd1", padding: "8px", color: "#000" };
const btn = {
  height: 36,
  border: "none",
  padding: "0 12px",
  fontWeight: 800,
  cursor: "pointer",
};
const btnPrimary = {
  background: "#E6FF3E",
  color: "#000",
  border: "none",
  padding: "8px 14px",
  fontWeight: 800,
  cursor: "pointer",
};
const btnDanger = {
  background: "#ffcccc",
  color: "#000",
  border: "1px solid #f00",
  padding: "6px 12px",
  cursor: "pointer",
};
